import React, { memo, useCallback, useState } from 'react'
import { FormikActions } from 'formik'
import { CHOICE_ID_OK } from '@purecarslabs/signal-components/ModalPrompt'
/* webpack-strip-block:removed */
import { LeadFormSpecFetcher } from '@app-src/data/lead-form-spec/fetchContexts'
import { AccountMakeBrandFetcher } from '@app-src/data/make-brand/fetchContexts'
import submitLead from '@app-src/data/leads/submitLead'
import { cookLeadForBackend } from '@app-src/data/leads/utils'
import { LeadContactType, LeadContextType } from '@app-src/data/leads/types'
import { debugLogForms } from '@app-src/debug'
import { FormContentPropsType, FormSubmitErrorFieldType } from './types'

type FormContentLoaderPropsType = {
  children?: React.ReactNode
  leadContext?: LeadContextType
} & FormContentPropsType

// this is the loader for the mobile form, which handles context fetchers and makes
// POSTs back to the BE
export const FormContentLoader = ({
  accountId,
  categoryName,
  children,
  countryCode,
  leadContext,
  mvhColors,
  onAfterSubmitLead,
  onNeedEnableChoice,
  formSubmitErrorText: initialFormSubmitErrorText,
  formSubmitFieldErrors: initialFormSubmitFieldErrors,
  ...rest
}: FormContentLoaderPropsType): JSX.Element => {
  const [formSubmitErrorText, setFormSubmitErrorText] = useState<
    string | undefined
  >(initialFormSubmitErrorText)
  const [formSubmitFieldErrors, setFormSubmitFieldErrors] = useState<
    Array<FormSubmitErrorFieldType> | undefined
  >(initialFormSubmitFieldErrors)

  const handleSubmit = useCallback(
    async (
      leadContact: LeadContactType,
      actions: FormikActions<LeadContactType>
    ): Promise<void> => {
      setFormSubmitErrorText(undefined)
      setFormSubmitFieldErrors(undefined)

      const enableChoice = onNeedEnableChoice()
      if (enableChoice) {
        enableChoice(CHOICE_ID_OK, false)
      }

      try {
        debugLogForms('submitting lead')

        const cookedLead = cookLeadForBackend(
          accountId,
          categoryName,
          countryCode,
          leadContact,
          leadContext
        )

        /* webpack-strip-block:removed */
          await submitLead({ lead: cookedLead })
          /* webpack-strip-block:removed */

        onAfterSubmitLead(cookedLead)
      } catch (submitErr) {
        debugLogForms('saw form submit error', submitErr)

        const errorMessage = (submitErr as Error).message

        try {
          const errorJson = JSON.parse(errorMessage)
          if (Array.isArray(errorJson)) {
            setFormSubmitFieldErrors(
              errorJson as Array<FormSubmitErrorFieldType>
            )
          }
        } catch (jsonErr) {
          setFormSubmitErrorText(errorMessage)
        }

        // re-enable to allow another submission
        if (enableChoice) {
          enableChoice(CHOICE_ID_OK, true)
        }
      } finally {
        actions.setSubmitting(false)
      }
    },
    [
      accountId,
      categoryName,
      countryCode,
      leadContext,
      onAfterSubmitLead,
      onNeedEnableChoice,
      setFormSubmitErrorText,
      setFormSubmitFieldErrors
    ]
  )

  return (
    <LeadFormSpecFetcher
      accountId={accountId}
      categoryName={categoryName}
      {...rest}
    >
      <AccountMakeBrandFetcher accountId={accountId} mvhColors={mvhColors}>
        {React.Children.map(children, child =>
          React.cloneElement(child as React.ReactElement, {
            onSubmit: handleSubmit,
            formSubmitErrorText,
            formSubmitFieldErrors,
            ...rest
          })
        )}
      </AccountMakeBrandFetcher>
    </LeadFormSpecFetcher>
  )
}

export default memo(FormContentLoader)
